import {
  ButtonComponentActionConfigVariant,
  DropdownComponentActionConfigButtonVariant,
  Namespace,
  Ref,
  Schedule,
  Sequence,
  Setting,
  SettingValue,
} from '@repo/sdk/models/components';

import { SUPPORTED_UNITS } from '@/helpers/unit-formatter/unit-formatter.constants';
import { normalizeDegrees } from '@/helpers/unit-formatter/unit-formatter.utils';
import { readingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-readings-stream';
import { settingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-settings-stream';
import { jotaiStore } from '@/providers/jotai/jotai-store';
import { LatestValue } from '@/types/reading-types';
import { isNullOrUndefined } from '@/utils/is-null-or-undefined';

import { EnhancedReading } from './subscriptions/use-get-readings-values';
import { EnhancedSetting } from './subscriptions/use-get-settings-values';

const TIME_UNITS: string[] = [
  SUPPORTED_UNITS.SEC,
  SUPPORTED_UNITS.MIN,
  SUPPORTED_UNITS.HRS,
  SUPPORTED_UNITS.DURATION,
  SUPPORTED_UNITS.TIMER,
];

/**
 * Thin wrapper around getSourceUnits for call sites that only have
 * raw displayUnits / units strings (e.g. bulk-edit cells) and don't
 * have a full EnhancedReading or EnhancedSetting object.
 */
export function resolveSourceUnits(displayUnits?: string, units?: string): string {
  return getSourceUnits({ displayUnits, units } as EnhancedReading);
}

export const getSourceUnits = (unitValue: EnhancedReading | EnhancedSetting): string => {
  const units = unitValue.units;
  const displayUnits = unitValue.displayUnits;

  const isUnitsNoneOrUndefined =
    isNullOrUndefined(units) || units === '' || units?.toLowerCase() === SUPPORTED_UNITS.NONE;
  const isTimeDisplayUnit = displayUnits && TIME_UNITS.includes(displayUnits.toLowerCase());

  // If units is None or undefined, but displayUnits is a time unit, return seconds (default time units)
  if (isUnitsNoneOrUndefined && isTimeDisplayUnit) {
    return SUPPORTED_UNITS.SEC;
  }

  // If units is not None or undefined, return units, otherwise return displayUnits
  return !!units && !isUnitsNoneOrUndefined ? units : (displayUnits ?? '');
};

// Converts a percent value to a decimal value
export const convertPercentToDecimal = (
  value: string | number | boolean | Schedule | Sequence | undefined
): number | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (typeof value === 'number') {
    return Number(value) / 100;
  }
  return undefined;
};

/** Returns a 0–1 gauge value for ux-animatics valve gauges when ref is present; undefined when absent. */
export function percentGaugeValueFromRef(ref: Ref | undefined): number | undefined {
  if (!ref) {
    return undefined;
  }
  return convertPercentToDecimal(getRefValue(ref)) ?? 0;
}

/** Returns normalized degrees (0–359) from a ref value; 0 when ref is absent or invalid. */
export function degreesFromRef(ref: Ref | undefined): number {
  if (!ref) {
    return 0;
  }
  const raw = getRefValue(ref);
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return normalizeDegrees(raw);
  }
  if (typeof raw === 'string' && raw !== '') {
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? normalizeDegrees(parsed) : 0;
  }
  return 0;
}

export const getRefValue = (
  ref: Ref
): string | number | boolean | Schedule | Sequence | undefined => {
  switch (ref.namespace) {
    case Namespace.Setting: {
      const setting = jotaiStore.get(settingsFamily({ settingId: ref.id } as Setting));
      return setting?.value?.[setting?.kind as keyof SettingValue];
    }
    case Namespace.Condition:
    case Namespace.ControlPoint:
    case Namespace.Metric:
    case Namespace.Output: {
      const reading = jotaiStore.get(readingsFamily({ pointId: ref.id } as LatestValue));
      return reading?.[reading?.kind as keyof LatestValue] as string | number | boolean | undefined;
    }
    default:
      console.warn(`Unknown namespace: ${ref.namespace}`);
      return undefined;
  }
};

export const mapLegacyButtonVariantToClassName = (
  variant: ButtonComponentActionConfigVariant | DropdownComponentActionConfigButtonVariant
) => {
  switch (variant) {
    case ButtonComponentActionConfigVariant.Default:
      return 'btn btn-default';
    case ButtonComponentActionConfigVariant.Tier2:
      return 'btn btn-tier2';
    case ButtonComponentActionConfigVariant.Tier3:
      return 'btn btn-tier3';
    case ButtonComponentActionConfigVariant.Brand:
      return 'btn btn-brand';
    case ButtonComponentActionConfigVariant.Destructive:
      return 'btn btn-destructive';
    case ButtonComponentActionConfigVariant.Inverse:
      return 'btn btn-inverse';
    case ButtonComponentActionConfigVariant.Link:
      return 'btn btn-link';
    case ButtonComponentActionConfigVariant.Transparent:
      return 'btn btn-transparent';
    default:
      return 'btn btn-default';
  }
};
